import { normalizeWidgetTheme, APIURL, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, apiGet, toggleWidgetTheme, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin } from '../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

export interface EsgRadarData {
    symbol: string;
    categories: string[];
    values: number[];
}

const ESG_RADAR_CATEGORIES = ['Social', 'Environmental', 'Governance'] as const;

export class TraderverseESGCompanyPerformanceRadar {
    private container: HTMLElement;
    private config: WidgetConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private shadowRoot: ShadowRoot;
    private esgRadarData: EsgRadarData | null = null;
    private currentChart: any;
    private widgetResizeObserver: ResizeObserver | null = null;

    constructor(config: WidgetConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.container = this.resolveContainer(config.container);
        this.shadowRoot = (this.container as any).shadowRoot || this.container.attachShadow({ mode: 'closed' });
        this.init();
        this.widgetResizeObserver = new ResizeObserver(() => this.updateDisplay());
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private getDefaultConfig(): Partial<WidgetConfig> {
        return {
      headerPinned: true,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true
        };
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private async init(): Promise<void> {
        this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        await this.attachStyles();
        await this.setupChart(this.config.symbol!);
        if (widget) widget.classList.remove('loading');
        cancelSelectEvent(this.shadowRoot);
        this.config.onReady?.();
    }

    private createWidget(): void {
        let themeClass = 'defaultDarkMode';
      let dataTheme = 'default-dark';
      
      if (this.config.theme === 'white') {
        themeClass = 'whiteMode';
        dataTheme = 'white';
      } else if (this.config.theme === 'black') {
        themeClass = 'blackMode';
        dataTheme = 'black';
      } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
        const tabStyles = `
        `;
        const widgetHtml = `
        ${tabStyles}
        <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
            <div class="headTop">
                <h4 class="title market">${this.getTitleIconSvg()} ESG Company Performance Radar</h4>
                <div class="actionCol">${getHeaderPinButtonHTML(this.config)}<i class="pi pi-ellipsis-v"></i></div>
            </div>
            <div class="inBody">
            <div class="optionsRow">
        ${this.config.hideSymbol ? '<div class="item dropStyle01"></div>' : `<div class="item dropStyle01">
                <div class="trader-sm-card" id="selected-symbol">
                ${getTraderSMCardHTML(this.config.stock ?? { symbol: this.config.symbol!, type: "stock", name: "" })}
                </div>
                <span id="symbol-dropdown-placeholder"></span>
                </div>`}
                </div>
                <div class="chartDv cryptoCompany" id="portfolioAssetTypeContainer"></div>
            </div>
        </div>`;
        this.shadowRoot.innerHTML = widgetHtml;
    setupHeaderPin(this.shadowRoot, this.config);
        if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
            const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
            const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
            if (placeholder && dropdown) {
              placeholder.replaceWith(dropdown);
            }
          }

    }

    onSelectChange(stock: any) {
        this.changeSymbol(stock.symbol, stock);
    }
    

    private async attachStyles(): Promise<void> {
        try {
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
            script.async = true;
            script.onload = () => resolve(true);
            this.shadowRoot.appendChild(script);
        });
    }

    private async setupChart(symbol: string): Promise<void> {
        try {
            const endDate = new Date();
            const startDate = new Date();
            startDate.setFullYear(endDate.getFullYear() - 2);
            const fmt = (d: Date) => d.toISOString().slice(0, 10);

            const res = await apiGet({
                method: 'POST',
                url: `${APIURL}/v1/company/fmp_esg_score?symbol=${encodeURIComponent(symbol)}&start_date=${fmt(startDate)}&end_date=${fmt(endDate)}`,
                body: { symbol, start_date: fmt(startDate), end_date: fmt(endDate) },
            });
            if (!res.ok) throw new Error('Failed to fetch ESG data');

            const json = await res.json();
            const rows = Array.isArray(json?.data) ? json.data : [];
            if (!rows.length) throw new Error('No ESG records found');

            const latest = [...rows].sort((a: any, b: any) => {
                const dateA = new Date(a.date || a.accepted_date || 0).getTime();
                const dateB = new Date(b.date || b.accepted_date || 0).getTime();
                return dateB - dateA;
            })[0];

            const values = [
                Number(latest.social_score) || 0,
                Number(latest.environmental_score) || 0,
                Number(latest.governance_score) || 0
            ];

            this.esgRadarData = {
                symbol,
                categories: [...ESG_RADAR_CATEGORIES],
                values
            };
            this.renderPortfolioChart();
        } catch (err) {
            console.error('Failed to fetch/setup chart data:', err);
            this.esgRadarData = null;
            const container = this.shadowRoot.querySelector('#portfolioAssetTypeContainer');
            if (container) container.innerHTML = '<div class="noData"><p>Failed to load data</p></div>';
        }
    }


    private updateDisplay(): void {
        if (!this.esgRadarData || !(window as any).echarts) {
            return;
        }
        this.renderPortfolioChart();
    }

    private renderPortfolioChart(): void {
        const container = this.shadowRoot.querySelector('#portfolioAssetTypeContainer');
        if (!container) return;

container.innerHTML = this.esgRadarData?.values?.length
    ? `<div id="portfolioChart" class="chart coverDv" data-testing-id="esg-performance-radar"></div>`
    : `<div class="noData"></div>`;
        this.drawChart();
    }


    private drawChart(): void {
        const echarts = (window as any).echarts;
        const container = this.shadowRoot.querySelector('#portfolioChart') as HTMLElement;
        const radar = this.esgRadarData;
        if (!container || !radar?.categories?.length || !radar?.values?.length) return;

        if (this.currentChart) {
            this.currentChart.dispose();
            this.currentChart = null;
        }

        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const textColor = getComputedStyle(widgetBox).getPropertyValue('--charts-text-color').trim() || '#C2C2C2';
        const fontSize = getComputedStyle(widgetBox).getPropertyValue('--font-size-sm').trim() || '14px';
        const labelFontSize = parseInt(fontSize, 10) || 14;
        const isCompact = container.clientWidth < 560;
        const gridColor = 'rgba(255, 255, 255, 0.18)';
        const accentColor = '#5907F9';

        const categories = radar.categories;
        const values = radar.values;
        const indicator = categories.map((name: string) => ({ name, max: 100 }));

        const formatScore = (v: number) => (Number.isFinite(v) ? Math.round(v) : 0);

        const option = {
            backgroundColor: 'transparent',

            tooltip: {
                trigger: 'item',
                backgroundColor: '#14121F',
                borderColor: 'rgba(89, 7, 249, 0.55)',
                borderWidth: 1,
                borderRadius: 8,
                padding: [10, 14],
                textStyle: {
                    color: '#FFFFFF',
                    fontSize: 13
                },
                formatter: (params: any) => {
                    const dimIndex = typeof params.dimensionIndex === 'number'
                        ? params.dimensionIndex
                        : categories.indexOf(params.name);
                    if (dimIndex < 0) return '';

                    const label = categories[dimIndex];
                    const score = formatScore(values[dimIndex]);
                    return `
                        <div style="display:flex;align-items:center;gap:8px;">
                            <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${accentColor};flex-shrink:0;"></span>
                            <span><b>${label}</b>&nbsp;&nbsp;${score}</span>
                        </div>
                    `;
                }
            },

            radar: {
                center: ['50%', '54%'],
                radius: isCompact ? '52%' : '62%',
                startAngle: 90,
                clockwise: true,
                indicator,
                shape: 'circle',
                splitNumber: 8,
                axisName: {
                    color: textColor,
                    fontSize: labelFontSize,
                    padding: [4, 6]
                },
                axisLine: {
                    lineStyle: { color: gridColor, width: 1 }
                },
                splitLine: {
                    lineStyle: { color: gridColor, type: 'dashed', width: 1 }
                },
                splitArea: { show: false },
                axisLabel: {
                    show: true,
                    color: 'rgba(255, 255, 255, 0.45)',
                    fontSize: 11,
                    margin: 6,
                    formatter: (value: number) => (value >= 20 && value % 10 === 0 ? String(value) : '')
                }
            },

            series: [
                {
                    name: 'ESG Performance',
                    type: 'radar',
                    data: [{ value: values, name: radar.symbol }],
                    symbol: 'circle',
                    symbolSize: 11,
                    lineStyle: { width: 2, color: accentColor },
                    itemStyle: {
                        color: accentColor,
                        borderColor: accentColor,
                        borderWidth: 0
                    },
                    areaStyle: { color: 'rgba(89, 7, 249, 0.32)' },
                    emphasis: {
                        focus: 'self',
                        lineStyle: { width: 2, color: accentColor },
                        itemStyle: {
                            color: accentColor,
                            borderColor: '#FFFFFF',
                            borderWidth: 2,
                            shadowBlur: 8,
                            shadowColor: 'rgba(89, 7, 249, 0.6)'
                        }
                    }
                }
            ]
        };

        this.currentChart = echarts.init(container);
        this.currentChart.setOption(option);

        this.currentChart.off('mousemove');
        this.currentChart.on('mousemove', (params: any) => {
            if (params.componentType !== 'series' || params.seriesType !== 'radar') return;
            const dimIndex = params.dimensionIndex ?? params.event?.target?.__dimIdx;
            if (typeof dimIndex !== 'number') return;

            this.currentChart.dispatchAction({
                type: 'showTip',
                seriesIndex: 0,
                dataIndex: 0,
                name: categories[dimIndex]
            });
        });
    }
      


    private changeSymbol(stockId: string, stock: any = null): void {
        changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, (stockId: any) => this.setupChart(stockId), (type: any, data: any) => this.emit(type, data));
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }
    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }
    public setSymbol(symbol: string): void {
        this.changeSymbol(symbol);
        const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
        if (dropdown) dropdown.value = symbol;
    }
    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }
  
    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
      if (!this.shadowRoot) return;
    
      this.config.theme = toggleWidgetTheme(
        this.shadowRoot,
        theme
      ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
    
      this.updateDisplay();
    }
    public destroy(): void {
        if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
        this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
    }
    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
    }

    private getTitleIconSvg(): string {
      return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }

}

declare global { interface Window { TraderverseESGCompanyPerformanceRadar: typeof TraderverseESGCompanyPerformanceRadar; } }
if (typeof window !== 'undefined') window.TraderverseESGCompanyPerformanceRadar = TraderverseESGCompanyPerformanceRadar;
